import { useState, useEffect } from 'react';
import api from '../../../api/client';

export default function ObligationsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [contractFilter, setContractFilter] = useState('');
  const [form, setForm] = useState({
    contract_id: '',
    name: '',
    standalone_selling_price: '',
    currency: 'USD',
    description: '',
    satisfaction_pattern: 'point_in_time',
  });

  async function load() {
    setLoading(true);
    try {
      const { data: d } = await api.get('/revrec/obligations', {
        params: contractFilter ? { contract_id: contractFilter } : {},
      });
      setItems(d.data?.items || d.items || []);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [contractFilter]);

  async function handleAdd(e) {
    e.preventDefault();
    if (!form.contract_id || !form.name || !form.standalone_selling_price) return;
    setBusy(true);
    setError(null);
    try {
      await api.post('/revrec/obligations', {
        contract_id: form.contract_id,
        name: form.name,
        standalone_selling_price_cents: Math.round(parseFloat(form.standalone_selling_price) * 100),
        currency: form.currency.toUpperCase(),
        description: form.description,
        satisfaction_pattern: form.satisfaction_pattern,
      });
      setForm({ contract_id: '', name: '', standalone_selling_price: '', currency: 'USD', description: '', satisfaction_pattern: 'point_in_time' });
      setShowForm(false);
      await load();
    } catch (e2) {
      setError(e2.message);
    } finally {
      setBusy(false);
    }
  }

  async function generateSchedule(obligationId) {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/revrec/obligations/${obligationId}/generate-schedule`);
      await load();
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setBusy(false);
    }
  }

  function fmtValue(cents, currency) {
    if (!cents) return '—';
    return `${currency} ${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
  }

  return (
    <section data-testid="revrec-obligations-page" data-loading={loading || busy ? 'true' : 'false'}>
      <header className="page-header">
        <div>
          <h1>Performance Obligations</h1>
          <p className="muted">Identify distinct obligations within each contract and generate recognition schedules.</p>
        </div>
        <button className="btn primary" data-testid="revrec-obligation-add" onClick={() => setShowForm((v) => !v)}>
          {showForm ? 'Cancel' : 'Add Obligation'}
        </button>
      </header>

      {error && <div className="alert error" data-testid="revrec-obligations-error">{error}</div>}

      <div className="card form-card row-form">
        <label>Filter by contract ID
          <input
            value={contractFilter}
            onChange={(e) => setContractFilter(e.target.value)}
            placeholder="contract id (leave blank for all)"
          />
        </label>
        {contractFilter && (
          <div className="actions">
            <button className="btn ghost" onClick={() => setContractFilter('')}>Clear filter</button>
          </div>
        )}
      </div>

      {showForm && (
        <form onSubmit={handleAdd} className="card form-card row-form">
          <label>Contract ID
            <input value={form.contract_id} onChange={(e) => setForm((f) => ({ ...f, contract_id: e.target.value }))} placeholder="contract id" />
          </label>
          <label>Obligation name
            <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. SaaS Subscription" />
          </label>
          <label>Standalone selling price
            <input type="number" step="0.01" value={form.standalone_selling_price} onChange={(e) => setForm((f) => ({ ...f, standalone_selling_price: e.target.value }))} placeholder="5000.00" />
          </label>
          <label>Currency
            <input value={form.currency} onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value }))} placeholder="USD" maxLength={3} />
          </label>
          <label>Satisfaction pattern
            <select value={form.satisfaction_pattern} onChange={(e) => setForm((f) => ({ ...f, satisfaction_pattern: e.target.value }))}>
              <option value="point_in_time">Point in time</option>
              <option value="over_time">Over time</option>
            </select>
          </label>
          <label>Description
            <input value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder="Optional details" />
          </label>
          <div className="actions">
            <button className="btn primary" type="submit" disabled={busy || !form.contract_id || !form.name || !form.standalone_selling_price}>Save</button>
          </div>
        </form>
      )}

      <div className="card">
        <table className="data-table" data-testid="revrec-obligation-list">
          <thead>
            <tr><th>Contract</th><th>Name</th><th>SSP</th><th>Pattern</th><th>Status</th><th></th></tr>
          </thead>
          <tbody>
            {items.map((ob) => (
              <tr key={ob.id} data-testid={`revrec-obligation-row-${ob.id}`}>
                <td>{ob.contract_id}</td>
                <td>{ob.name}</td>
                <td>{fmtValue(ob.standalone_selling_price_cents, ob.currency)}</td>
                <td>{ob.satisfaction_pattern === 'over_time' ? 'Over time' : 'Point in time'}</td>
                <td><span className={`badge ${ob.schedule_generated ? 'success' : ''}`}>{ob.schedule_generated ? 'scheduled' : 'pending'}</span></td>
                <td>
                  <button
                    className="btn ghost"
                    data-testid="revrec-schedule-generate"
                    disabled={busy || ob.schedule_generated}
                    onClick={() => generateSchedule(ob.id)}
                  >
                    Generate schedule
                  </button>
                </td>
              </tr>
            ))}
            {!loading && items.length === 0 && (
              <tr><td colSpan={6} className="muted">No obligations found.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
